import React from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Phone, CheckCircle2, Zap, ShieldCheck, Clock } from "lucide-react";
import { BRAND, WHATSAPP_LINK } from "@/lib/constants";

const HIGHLIGHTS = [
  { icon: Zap, title: "تنفيذ خلال دقائق", desc: "استلم سيولتك فوراً" },
  { icon: ShieldCheck, title: "سرية تامة", desc: "بياناتك بأمان" },
  { icon: Clock, title: "دعم طوال الأسبوع", desc: "نرد عليك فوراً" },
];

export default function ContactSection() {
  return (
    <section id="contact" className="py-20 md:py-28 bg-brand-bg" data-testid="contact-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-12 fade-up">
          <div className="inline-block text-brand-gold font-bold text-sm tracking-widest uppercase mb-3">
            تواصل معنا
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-brand-green leading-tight">
            اطلب سيولتك الآن عبر الواتساب
          </h2>
          <p className="mt-3 text-base md:text-lg text-brand-ink/70 max-w-2xl mx-auto">
            تواصل معنا مباشرة وسيقوم فريقنا بإتمام طلبك خلال دقائق معدودة — بدون نماذج أو انتظار.
          </p>
        </div>

        <div className="relative bg-gradient-to-br from-brand-green to-brand-green-light rounded-[32px] p-8 md:p-14 text-white shadow-[0_30px_80px_-20px_rgba(15,61,46,0.35)] ring-1 ring-brand-gold/30 overflow-hidden fade-up">
          <div className="grain-overlay" />
          <div className="absolute -top-24 -start-24 w-72 h-72 bg-brand-gold/15 blur-3xl rounded-full" />
          <div className="absolute -bottom-24 -end-24 w-80 h-80 bg-brand-gold/10 blur-3xl rounded-full" />

          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-gold/15 border border-brand-gold/40 mb-5">
                <CheckCircle2 className="w-4 h-4 text-brand-gold-light" />
                <span className="text-xs font-bold text-brand-gold-light">جاهزون للرد فوراً</span>
              </div>

              <h3 className="font-heading text-2xl md:text-3xl font-extrabold mb-3 leading-snug">
                راسلنا برسالة واحدة — نرد عليك خلال دقائق
              </h3>
              <p className="text-white/80 text-base leading-relaxed mb-7">
                أرسل لنا اسمك والمبلغ الذي تحتاجه، وفريقنا سيتولى الباقي نيابةً عنك بكل سرية واحترافية.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  asChild
                  data-testid="contact-whatsapp-btn"
                  className="h-14 px-8 rounded-full bg-[#25D366] hover:bg-[#1fb858] text-white font-extrabold gap-2 text-base shadow-xl hover:scale-[1.02] transition-transform"
                >
                  <a href={WHATSAPP_LINK("السلام عليكم، أرغب في طلب سيولة مالية من ميرال كروب. المبلغ المطلوب: ")} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-5 h-5" />
                    اطلب الآن عبر واتساب
                  </a>
                </Button>

                <Button
                  asChild
                  variant="outline"
                  data-testid="contact-phone-btn"
                  className="h-14 px-6 rounded-full bg-transparent border-white/30 hover:bg-white/10 text-white font-bold gap-2 text-base"
                >
                  <a href={`tel:${BRAND.phone}`} dir="ltr">
                    <Phone className="w-5 h-5" />
                    {BRAND.phone}
                  </a>
                </Button>
              </div>

              <p className="mt-6 text-xs text-white/60">
                خدمة متاحة للمواطن والمقيم — معاملة شرعية موثوقة.
              </p>
            </div>

            <div className="space-y-3">
              {HIGHLIGHTS.map((h, i) => (
                <div
                  key={i}
                  data-testid={`contact-highlight-${i + 1}`}
                  className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-gold/20 ring-1 ring-brand-gold/40 flex items-center justify-center shrink-0">
                    <h.icon className="w-6 h-6 text-brand-gold-light" />
                  </div>
                  <div>
                    <div className="font-heading font-extrabold text-white">{h.title}</div>
                    <div className="text-sm text-white/70">{h.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
